import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { downloadFileHelper } from "features/fileManagement/downloadFileHelper";
import { Base64 } from "js-base64";
import { useEffect, useState } from "react";
import { getSingleFileUploadViaFileEntryApi } from "services";

function getMimeType(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "csv":
      return "text/csv";
    default:
      return "application/octet-stream";
  }
}

function isImage(fileName: string): boolean {
  const ext = fileName.split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext ?? "");
}

function isPreviewable(fileName: string): boolean {
  const ext = fileName.split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "pdf"].includes(ext ?? "");
}

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;

interface Props {
  open: boolean;
  fileEntryGuid: string;
  fileName: string;
  onClose: () => void;
}

export default function EvidenceViewerDialog({
  open,
  fileEntryGuid,
  fileName,
  onClose,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const { notify } = useNotification();
  const showImageViewer = isImage(fileName);
  const canPreview = isPreviewable(fileName);

  useEffect(() => {
    if (!open || !fileEntryGuid || !canPreview) return;
    let cancelled = false;
    setLoading(true);
    setBlobUrl(null);
    setZoom(1);

    async function loadFile() {
      try {
        const fileUpload = await getSingleFileUploadViaFileEntryApi({
          guid: fileEntryGuid,
        });

        if (cancelled) return;

        const data = Base64.toUint8Array(fileUpload.file);
        const mimeType = getMimeType(fileName);
        const blob = new Blob([new Uint8Array(data)], { type: mimeType });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setLoading(false);
          notify("Error loading file", "error");
        }
      }
    }

    void loadFile();
    return () => {
      cancelled = true;
    };
  }, [open, fileEntryGuid]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const handleClose = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          component="span"
          sx={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mr: 2,
          }}
        >
          {fileName || "Evidence"}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            flexShrink: 0,
            alignItems: "center",
          }}
        >
          {showImageViewer && (
            <>
              <IconButton
                size="small"
                onClick={() =>
                  setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))
                }
                disabled={zoom <= MIN_ZOOM}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ minWidth: 40, textAlign: "center" }}
              >
                {Math.round(zoom * 100)}%
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))
                }
                disabled={zoom >= MAX_ZOOM}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            onClick={() => {
              void downloadFileHelper(fileEntryGuid, notify);
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {!canPreview ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Typography color="text.secondary">
              Preview not available. Want to download instead?
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => {
                void downloadFileHelper(fileEntryGuid, notify);
              }}
            >
              Download
            </Button>
          </Box>
        ) : loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : blobUrl && showImageViewer ? (
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              alignItems: zoom <= 1 ? "center" : "flex-start",
              justifyContent: zoom <= 1 ? "center" : "flex-start",
              p: 2,
              bgcolor: "#f5f5f5",
            }}
          >
            <img
              src={blobUrl}
              alt={fileName || "Evidence"}
              style={{
                maxWidth: zoom === 1 ? "100%" : "none",
                maxHeight: zoom === 1 ? "100%" : "none",
                width: zoom !== 1 ? `${zoom * 100}%` : undefined,
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        ) : blobUrl ? (
          <iframe
            src={blobUrl}
            title={fileName || "Evidence viewer"}
            style={{
              flex: 1,
              width: "100%",
              border: "none",
            }}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              Unable to load file preview
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
